import { lazy, Suspense, useEffect } from 'react'
import { createBrowserRouter, Outlet, RouterProvider, ScrollRestoration, useRouteError } from 'react-router-dom'
import { reportGlobalError } from '@/components/templates/error-boundary/GlobalErrorBoundary'
import { ModuleNameSyncer } from '@/layouts/ModuleNameSyncer'
import coreRoutes from './core/routes'
import wmsRoutes from './wms/routes'

// import dmsRoutes from './dms/routes'
// import oeeRoutes from './oee/routes'
// import prtsRoutes from './prts/routes'
// import qmsRoutes from './qms/routes'

const NotFound = lazy(() => import('@/modules/error/pages/404'))
const SolutionsLayout = lazy(() => import('@/modules'))
const ConfirmAOPage = lazy(() => import('@/modules/public/confirm-ao'))
const DeliveryConfirmationPage = lazy(() => import('@/modules/public/delivery-confirmation'))
const ArrivalOrderPrintQRPage = lazy(() => import('@/modules/public/arrival-order-print-qr'))

/**
 * Bridge component: intercept error yang di-catch React Router
 * lalu teruskan ke GlobalErrorBoundary via reportGlobalError.
 *
 * Kenapa perlu ini: React Router punya internal error boundary sendiri
 * yang intercept render errors sebelum bubble up ke React tree di atasnya.
 * Tanpa errorElement ini, error akan stuck di React Router dan tidak
 * pernah sampai ke GlobalErrorBoundary.
 */
export function RouterErrorBoundary() {
  const error = useRouteError()

  useEffect(() => {
    if (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      reportGlobalError(err)
    }
  }, [error])

  // Return null — GlobalErrorBoundary yang akan render UI error-nya
  // setelah reportGlobalError dipanggil
  return null
}

function SolutionsRoute() {
  const routes = createBrowserRouter([
    // Public routes - no authentication required
    {
      path: 'public/confirm-delivery',
      element: <ConfirmAOPage />
    },
    {
      path: 'public/delivery-confirmation',
      element: <DeliveryConfirmationPage />
    },
    {
      path: 'public/arrival-order-print-qr',
      element: <ArrivalOrderPrintQRPage />
    },
    {
      path: 'solutions',
      errorElement: <RouterErrorBoundary />,
      element: (
        <>
          {/* Pindah halaman (pathname) mulai dari atas — header halaman tidak tertinggal di balik topbar sticky.
              Ganti query (?tab=…) tidak me-reset scroll; tombol Back memulihkan posisi sebelumnya. */}
          <ScrollRestoration getKey={location => location.pathname} />
          <ModuleNameSyncer />
          <SolutionsLayout />
        </>
      ),
      children: [
        {
          handle: { name: 'Core System' },
          path: 'core',
          children: coreRoutes
        },
        {
          handle: { name: 'Warehouse Management System' },
          path: 'wms',
          children: wmsRoutes
        },
        // {
        //   handle: { name: 'Daily Management System' },
        //   path: 'dms',
        //   children: dmsRoutes
        // },
        // {
        //   handle: { name: 'Quality Management System' },
        //   path: 'qms',
        //   children: qmsRoutes
        // },
        // {
        //   handle: { name: 'PRTS' },
        //   path: 'prts',
        //   children: prtsRoutes
        // },
        // {
        //   handle: { name: 'OEE - Overall Equipment Effectiveness' },
        //   path: 'oee',
        //   children: oeeRoutes
        // },
        {
          path: '*',
          element: <NotFound />
        }
      ]
    },
    {
      path: '*',
      element: <Outlet />
    }
  ])

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <RouterProvider router={routes} />
    </Suspense>
  )
}

export default SolutionsRoute
